import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { ConfigurationService } from '@configuration/service/configuration.service';

import { MODULE_ROUTES } from '../constants/module-routes.const';

export const featureFlagGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const configurationService = inject(ConfigurationService);
  const router = inject(Router);

  const requiredActiveFeatures = route?.data?.requiredActiveFeatures ?? [];

  if (!requiredActiveFeatures.length) {
    console.error(`ROUTER ERROR: Feature flag guard missing 'requiredActiveFeatures' list - ${router.url}`);
    return of(true);
  }

  // Ensure the feature-flag config is actually loaded BEFORE evaluating. On a hard refresh
  // (or deep-link), this guard runs during app bootstrap — before `fetchFeatureConfigurationData`
  // has populated `featureConfiguration$`, which is a BehaviorSubject seeded with `[]`.
  // `isFeatureActive$` only filters on truthiness, and `[]` is truthy, so it would emit an
  // all-false state against the empty seed and bounce the user to no-access (works fine on
  // in-app navigation because the config is already loaded by then). `getFeatureFlagConfigurationData$()`
  // triggers + awaits the real load (HTTP if not yet cached), mirroring `roleAccessChildGuard`.
  return configurationService.getFeatureFlagConfigurationData$().pipe(
    switchMap(() => configurationService.isFeatureActive$(requiredActiveFeatures)),
    take(1),
    map(featureState => {
      const isEnabled = Object.values(featureState).every(value => value === true);
      if (!isEnabled) {
        router.navigate([`/${MODULE_ROUTES.MAIN}`, MODULE_ROUTES.NO_ACCESS], { replaceUrl: true });
      }
      return isEnabled;
    })
  );
};
